import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, Users, ShieldCheck, ShieldSlash, MagnifyingGlass,
  CreditCard, CheckCircle, XCircle, Warning, Clock, CalendarPlus,
  Calendar, ArrowsClockwise, UserPlus, X, Key, Trash, ListBullets,
} from "@phosphor-icons/react";
import AdminSection from "../../components/admin/AdminSection";
import apiClient from "../../lib/api";
import { toast } from "sonner";

const PLAN_STYLE = {
  free:     { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20", label: "Free" },
  premium:  { cls: "bg-blue-500/10 text-blue-400 border-blue-500/20", label: "Premium" },
  vip:      { cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "VIP" },
};

const STATUS_STYLE = {
  active:   { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Activo", Icon: CheckCircle },
  trialing: { cls: "bg-blue-500/10 text-blue-400 border-blue-500/20", label: "Trial", Icon: Clock },
  canceled: { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Cancelado", Icon: XCircle },
  inactive: { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20", label: "Inactivo", Icon: XCircle },
  expired:  { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Vencido", Icon: XCircle },
  past_due: { cls: "bg-orange-500/10 text-orange-400 border-orange-500/20", label: "Pago pendiente", Icon: Warning },
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Subscription management modal state
  const [subModal, setSubModal] = useState(null);
  const [subMode, setSubMode] = useState("days");
  const [subDays, setSubDays] = useState(30);
  const [subDate, setSubDate] = useState("");
  const [subPlan, setSubPlan] = useState("premium");
  const [subLoading, setSubLoading] = useState(false);

  // Create user modal state
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", plan: "premium", expires_at: "" });
  const [createLoading, setCreateLoading] = useState(false);

  // Change password modal state
  const [pwModal, setPwModal] = useState(null); // user object
  const [newPassword, setNewPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/users");
      setUsers(r.data.users || []);
    } catch (err) {
      console.error("[AdminUsers] load failed:", err?.response?.data || err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (user) => {
    const action = user.is_suspended ? "activate" : "suspend";
    const confirm = window.confirm(
      action === "suspend"
        ? `¿Suspender a ${user.name}? Perderá acceso inmediatamente.`
        : `¿Reactivar a ${user.name}?`
    );
    if (!confirm) return;
    setActionLoading(user.id);
    try {
      await apiClient.put(`/admin/users/${user.id}/status`, { action });
      toast.success(action === "suspend" ? `${user.name} suspendido` : `${user.name} reactivado`);
      await load();
    } catch (err) {
      toast.error("Error al cambiar estado");
      console.error(err?.response?.data || err.message);
    }
    setActionLoading(null);
  };

  const openSubModal = (user) => {
    setSubModal(user);
    setSubMode("days");
    setSubDays(30);
    setSubPlan(user.plan !== "free" ? user.plan : "premium");
    const d = new Date(); d.setDate(d.getDate() + 30);
    setSubDate(d.toISOString().split("T")[0]);
  };

  const applySubscription = async () => {
    if (!subModal) return;
    setSubLoading(true);
    try {
      if (subMode === "days") {
        await apiClient.post(`/admin/users/${subModal.id}/extend`, { days: subDays, plan: subPlan });
        toast.success(`✅ Suscripción extendida ${subDays} días para ${subModal.name}`);
      } else {
        await apiClient.post(`/admin/users/${subModal.id}/set-subscription`, {
          expires_at: subDate, plan: subPlan,
        });
        toast.success(`✅ Suscripción actualizada para ${subModal.name} — vence el ${subDate}`);
      }
      setSubModal(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar suscripción");
    }
    setSubLoading(false);
  };

  const rescueTrialFromStripe = async (user) => {
    const ok = window.confirm(
      `¿Rescatar el trial de Stripe para ${user.email}?\n\n` +
      `Esta acción consulta Stripe con el email del usuario, encuentra su última suscripción y activa el trial en la DB.\n\n` +
      `Úsalo cuando el usuario completó el pago con tarjeta pero quedó "inactivo".`
    );
    if (!ok) return;
    setActionLoading(user.id);
    try {
      const r = await apiClient.post("/admin/trial/rescue-by-email", { email: user.email });
      toast.success(`✅ Trial activado: ${r.data.status} — vence ${r.data.expires_at?.slice(0, 10) || "?"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo rescatar el trial");
    }
    setActionLoading(null);
  };

  const deleteUser = async (user) => {
    const confirmText = `ELIMINAR ${user.email}`;
    const typed = window.prompt(
      `⚠️ ELIMINAR PERMANENTEMENTE a ${user.email}\n\n` +
      `Esto borra el usuario, sus pagos y sus mensajes de soporte. NO se puede deshacer.\n\n` +
      `Para confirmar, escribe:\n${confirmText}`
    );
    if (typed !== confirmText) {
      if (typed !== null) toast.error("Texto de confirmación incorrecto. No se eliminó.");
      return;
    }
    setActionLoading(user.id);
    try {
      await apiClient.delete(`/admin/users/${user.id}`);
      toast.success(`🗑️ Usuario ${user.email} eliminado`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar usuario");
    }
    setActionLoading(null);
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Nombre, email y contraseña son obligatorios");
      return;
    }
    setCreateLoading(true);
    try {
      const payload = {
        name: createForm.name,
        email: createForm.email,
        password: createForm.password,
        plan: createForm.plan,
        ...(createForm.expires_at && { expires_at: createForm.expires_at }),
      };
      const r = await apiClient.post("/admin/users", payload);
      toast.success(`✅ Usuario ${r.data.email} creado correctamente`);
      setCreateModal(false);
      setCreateForm({ name: "", email: "", password: "", plan: "premium", expires_at: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear usuario");
    }
    setCreateLoading(false);
  };

  const handleChangePassword = async () => {
    if (!pwModal || newPassword.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setPwLoading(true);
    try {
      const r = await apiClient.post(`/admin/users/${pwModal.id}/set-password`, { password: newPassword });
      toast.success(r.data.message || "Contraseña actualizada");
      setPwModal(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar contraseña");
    }
    setPwLoading(false);
  };

  const openPayments = async (user) => {
    setSelectedUser(user);
    setPaymentsLoading(true);
    setPayments([]);
    try {
      const r = await apiClient.get(`/admin/users/${user.id}/payments`);
      setPayments(r.data.payments || []);
    } catch (err) { console.error(err?.response?.data || err.message); }
    setPaymentsLoading(false);
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const summary = {
    total: users.length,
    active: users.filter(u => u.subscription_status === "active").length,
    trial: users.filter(u => u.subscription_status === "trialing").length,
    suspended: users.filter(u => u.is_suspended).length,
  };

  function daysToExpiry(expiresAt) {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt) - Date.now();
    if (ms < 0) return Math.floor(ms / 86400000);  // negative → expired N days ago
    return Math.ceil(ms / 86400000);                // positive → use ceil so 23h59m == 1 day
  }

  function hoursToExpiry(expiresAt) {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt) - Date.now();
    if (ms < 0 || ms >= 86400000) return null;
    return Math.ceil(ms / 3600000);
  }

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-sm" style={{ background: "rgba(10,10,12,0.85)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 flex items-center gap-4">
          <Link to="/admin" className="text-zinc-500 hover:text-white transition-colors">
            <CaretLeft weight="bold" className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Users weight="duotone" className="w-5 h-5 text-yellow-400" />
            <span className="font-bold text-lg">Gestión de Usuarios</span>
          </div>
          <button onClick={load} className="ml-auto text-zinc-500 hover:text-white" title="Actualizar">
            <ArrowsClockwise className="w-4 h-4" />
          </button>
          <button
            data-testid="create-user-btn"
            onClick={() => setCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-black transition-all hover:brightness-110"
            style={{ background: "#D4AF37" }}
          >
            <UserPlus className="w-4 h-4" />
            Crear Usuario
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        {/* Summary */}
        <AdminSection icon={Users} title="Estadísticas de usuarios" subtitle="Resumen general de la plataforma" color="sky">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total usuarios", value: summary.total, color: "text-white" },
            { label: "Activos", value: summary.active, color: "text-emerald-400" },
            { label: "En trial", value: summary.trial, color: "text-blue-400" },
            { label: "Suspendidos", value: summary.suspended, color: "text-red-400" },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-4 text-center" style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.07)" }}>
              <div className={`text-3xl font-black number-ticker ${s.color}`}>{s.value}</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
          </div>
        </AdminSection>

        {/* Search */}
        <AdminSection icon={MagnifyingGlass} title="Buscar usuarios" subtitle="Filtra por nombre o email" color="violet">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="admin-users-search"
            className="input-field pl-10 w-full max-w-sm"
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        </AdminSection>

        {/* ── MOBILE CARDS (< md) ──────────────────────────────── */}
        <AdminSection icon={ListBullets} title="Usuarios" subtitle="Toca un usuario para ver sus acciones" color="emerald">
        <div className="md:hidden space-y-2">
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Cargando usuarios...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">No se encontraron usuarios.</div>
          ) : filtered.map(u => {
            const planS  = PLAN_STYLE[u.plan] || PLAN_STYLE.free;
            const subS   = STATUS_STYLE[u.subscription_status] || STATUS_STYLE.inactive;
            const days   = daysToExpiry(u.subscription_expires_at);
            const hours  = hoursToExpiry(u.subscription_expires_at);
            const isSusp = u.is_suspended;
            const isOpen = expandedId === u.id;
            const expiryText = days === null ? null
              : days < 0 ? `Vencido hace ${Math.abs(days)}d`
              : hours !== null ? `Vence en ${hours}h`
              : `Vence en ${days}d`;

            return (
              <div key={u.id} data-testid={`admin-user-card-${u.id}`}
                className="rounded-2xl overflow-hidden"
                style={{ background: "#16161A", border: `1px solid ${isSusp ? "rgba(239,68,68,0.2)" : isOpen ? "rgba(212,175,55,0.25)" : "rgba(255,255,255,0.06)"}`, opacity: isSusp ? 0.8 : 1 }}>

                {/* Card header — always visible, tap to expand */}
                <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                  onClick={() => setExpandedId(isOpen ? null : u.id)}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center text-sm font-bold text-yellow-400 flex-shrink-0">
                    {(u.name || u.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white text-sm truncate">
                      {u.name}
                      {isSusp && <span className="ml-1 text-[10px] text-red-400">(BLOQUEADO)</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`chip text-[10px] ${subS.cls}`}>{subS.label}</span>
                    {expiryText && (
                      <span className={`text-[10px] font-mono font-bold ${days < 0 ? "text-red-400" : days <= 3 ? "text-orange-400" : "text-zinc-400"}`}>
                        {expiryText}
                      </span>
                    )}
                  </div>
                  <div className={`ml-2 text-zinc-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 10.5L2.5 5 4 3.5l4 4 4-4L13.5 5z"/></svg>
                  </div>
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div className="border-t px-4 py-4 space-y-4" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.2)" }}>
                    {/* Info row */}
                    <div className="flex flex-wrap gap-2">
                      <span className={`chip text-[11px] ${planS.cls}`}>{planS.label}</span>
                      {u.subscription_expires_at && (
                        <span className="text-xs text-zinc-400">Vence: <strong className="text-white">{new Date(u.subscription_expires_at).toLocaleDateString("es-ES")}</strong></span>
                      )}
                    </div>

                    {/* Action grid */}
                    {u.role !== "admin" && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          data-testid={`admin-user-extend-${u.id}`}
                          onClick={() => { setExpandedId(null); openSubModal(u); }}
                          className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-yellow-500/25 text-yellow-400 bg-yellow-500/8 active:bg-yellow-500/20 transition-all">
                          <CalendarPlus className="w-4 h-4" />Gestionar
                        </button>
                        <button
                          data-testid={`admin-user-toggle-${u.id}`}
                          onClick={() => { setExpandedId(null); toggleStatus(u); }}
                          disabled={actionLoading === u.id}
                          className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border transition-all disabled:opacity-50 ${
                            isSusp
                              ? "border-emerald-500/25 text-emerald-400 bg-emerald-500/8 active:bg-emerald-500/20"
                              : "border-red-500/25 text-red-400 bg-red-500/8 active:bg-red-500/20"
                          }`}>
                          {isSusp ? <><ShieldCheck className="w-4 h-4" />Desbloquear</> : <><ShieldSlash className="w-4 h-4" />Bloquear</>}
                        </button>
                        <button
                          data-testid={`admin-user-password-${u.id}`}
                          onClick={() => { setExpandedId(null); setPwModal(u); setNewPassword(""); }}
                          className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-blue-500/25 text-blue-400 bg-blue-500/8 active:bg-blue-500/20 transition-all">
                          <Key className="w-4 h-4" />Clave
                        </button>
                        <button
                          data-testid={`admin-user-payments-${u.id}`}
                          onClick={() => { setExpandedId(null); openPayments(u); }}
                          className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-white/[0.1] text-zinc-400 bg-white/[0.03] active:bg-white/[0.08] transition-all">
                          <CreditCard className="w-4 h-4" />Pagos
                        </button>
                        {(u.subscription_status === "inactive" || u.pending_card_setup) && (
                          <button
                            data-testid={`admin-user-rescue-${u.id}`}
                            onClick={() => { setExpandedId(null); rescueTrialFromStripe(u); }}
                            disabled={actionLoading === u.id}
                            className="col-span-2 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-purple-500/25 text-purple-300 bg-purple-500/8 active:bg-purple-500/20 transition-all disabled:opacity-50">
                            <ArrowsClockwise className="w-4 h-4" />Rescatar Trial de Stripe
                          </button>
                        )}
                        <button
                          data-testid={`admin-user-delete-${u.id}`}
                          onClick={() => { setExpandedId(null); deleteUser(u); }}
                          disabled={actionLoading === u.id}
                          className="col-span-2 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold border border-red-700/30 text-red-400/70 bg-transparent active:bg-red-700/10 transition-all disabled:opacity-50">
                          <Trash className="w-3.5 h-3.5" />Eliminar usuario permanentemente
                        </button>
                      </div>
                    )}
                    {u.role === "admin" && (
                      <p className="text-xs text-zinc-600 text-center py-2">Cuenta de administrador — acciones restringidas</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── DESKTOP TABLE (md+) ──────────────────────────────── */}
        <div className="hidden md:block rounded-2xl overflow-hidden" style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.07)" }}>
          {loading ? (
            <div className="p-12 text-center text-zinc-500">Cargando usuarios...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">No se encontraron usuarios.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    {["Usuario", "Plan", "Estado", "Vencimiento", "Acciones"].map(h => (
                      <th key={h} className="px-5 py-3.5 text-left text-[11px] text-zinc-500 uppercase tracking-wider font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => {
                    const planS  = PLAN_STYLE[u.plan] || PLAN_STYLE.free;
                    const subS   = STATUS_STYLE[u.subscription_status] || STATUS_STYLE.inactive;
                    const days   = daysToExpiry(u.subscription_expires_at);
                    const hours  = hoursToExpiry(u.subscription_expires_at);
                    const isSusp = u.is_suspended;
                    return (
                      <tr key={u.id} data-testid={`admin-user-row-${u.id}`}
                        className="transition-colors hover:bg-white/[0.02]"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", opacity: isSusp ? 0.65 : 1 }}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center text-xs font-bold text-yellow-400 flex-shrink-0">
                              {(u.name || u.email || "?")[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-white truncate max-w-[160px]">
                                {u.name}
                                {isSusp && <span className="ml-2 text-[10px] text-red-400 font-normal">(BLOQUEADO)</span>}
                              </div>
                              <div className="text-xs text-zinc-500 truncate max-w-[160px]">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`chip text-[11px] ${planS.cls}`}>{planS.label}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`chip text-[11px] ${subS.cls}`}>{subS.label}</span>
                        </td>
                        <td className="px-5 py-4">
                          {days !== null ? (
                            <span className={`text-xs font-mono font-bold ${days < 0 ? "text-red-400" : days <= 1 ? "text-orange-400" : "text-zinc-300"}`}>
                              {days < 0
                                ? `Vencido ${Math.abs(days)}d`
                                : hours !== null
                                  ? `${hours}h`
                                  : `${days} días`}
                            </span>
                          ) : <span className="text-zinc-600 text-xs">—</span>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {u.role !== "admin" && (
                              <>
                                <button
                                  data-testid={`admin-user-extend-${u.id}`}
                                  onClick={() => openSubModal(u)}
                                  className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-amber-500/25 text-amber-400 bg-amber-500/8 hover:bg-amber-500/18 transition-all">
                                  <CalendarPlus className="w-3.5 h-3.5" />Gestionar
                                </button>
                                <button
                                  data-testid={`admin-user-toggle-${u.id}`}
                                  onClick={() => toggleStatus(u)}
                                  disabled={actionLoading === u.id}
                                  title={isSusp ? "Desbloquear usuario" : "Bloquear usuario"}
                                  className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all disabled:opacity-40 ${
                                    isSusp
                                      ? "border-emerald-500/25 text-emerald-400 bg-emerald-500/8 hover:bg-emerald-500/18"
                                      : "border-white/[0.08] text-zinc-500 hover:text-red-400 hover:border-red-500/25 hover:bg-red-500/8"
                                  }`}>
                                  {isSusp ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldSlash className="w-3.5 h-3.5" />}
                                  {isSusp ? "Activar" : "Bloquear"}
                                </button>
                                <button
                                  data-testid={`admin-user-password-${u.id}`}
                                  onClick={() => { setPwModal(u); setNewPassword(""); }}
                                  title="Cambiar contraseña"
                                  className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-zinc-500 hover:text-white hover:border-white/20 transition-all">
                                  <Key className="w-3.5 h-3.5" />Clave
                                </button>
                                <button
                                  data-testid={`admin-user-payments-${u.id}`}
                                  onClick={() => openPayments(u)}
                                  title="Ver historial de pagos"
                                  className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-zinc-500 hover:text-white hover:border-white/20 transition-all">
                                  <CreditCard className="w-3.5 h-3.5" />Pagos
                                </button>
                                {(u.subscription_status === "inactive" || u.pending_card_setup) && (
                                  <button
                                    data-testid={`admin-user-rescue-${u.id}`}
                                    onClick={() => rescueTrialFromStripe(u)}
                                    disabled={actionLoading === u.id}
                                    title="Activar desde Stripe si el usuario pagó pero quedó inactivo"
                                    className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-zinc-500 hover:text-purple-400 hover:border-purple-500/25 hover:bg-purple-500/8 transition-all disabled:opacity-40">
                                    <ArrowsClockwise className="w-3.5 h-3.5" />Rescatar
                                  </button>
                                )}
                                <button
                                  data-testid={`admin-user-delete-${u.id}`}
                                  onClick={() => deleteUser(u)}
                                  disabled={actionLoading === u.id}
                                  title="Eliminar usuario permanentemente"
                                  className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-white/[0.08] text-zinc-600 hover:text-red-400 hover:border-red-500/25 hover:bg-red-500/8 transition-all disabled:opacity-40">
                                  <Trash className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                            {u.role === "admin" && (
                              <span className="text-xs text-zinc-700 italic">Admin</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </AdminSection>
        {/* end desktop table */}
      </div>

      {/* ── Gestionar Suscripción Modal ──────────────────────────────────────── */}
      {subModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setSubModal(null); }}>
          <div className="rounded-2xl p-6 w-full max-w-md" style={{ background: "#1C1C22", border: "1px solid rgba(212,175,55,0.25)", boxShadow: "0 20px 60px rgba(0,0,0,0.7)" }}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-black text-xl text-white">Gestionar suscripción</h3>
                <p className="text-sm text-zinc-400 mt-0.5">{subModal.name} · {subModal.email}</p>
                {subModal.subscription_expires_at && (
                  <p className="text-xs mt-1" style={{ color: "#D4AF37" }}>
                    Vence actualmente: {new Date(subModal.subscription_expires_at).toLocaleDateString("es-ES")}
                  </p>
                )}
              </div>
              <button onClick={() => setSubModal(null)} className="text-zinc-500 hover:text-white mt-1">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Mode selector */}
            <div className="flex gap-2 mb-5">
              <button
                onClick={() => setSubMode("days")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                  subMode === "days" ? "text-black" : "text-zinc-500 border border-white/[0.07]"
                }`}
                style={subMode === "days" ? { background: "linear-gradient(135deg, #D4AF37, #B8962E)", border: "1px solid rgba(212,175,55,0.5)" } : {}}>
                <ArrowsClockwise className="w-3.5 h-3.5" />
                Agregar días
              </button>
              <button
                onClick={() => setSubMode("date")}
                data-testid="sub-mode-date"
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                  subMode === "date" ? "text-black" : "text-zinc-500 border border-white/[0.07]"
                }`}
                style={subMode === "date" ? { background: "linear-gradient(135deg, #D4AF37, #B8962E)", border: "1px solid rgba(212,175,55,0.5)" } : {}}>
                <Calendar className="w-3.5 h-3.5" />
                Fecha exacta
              </button>
            </div>

            <div className="space-y-4">
              {/* Mode A: Add days */}
              {subMode === "days" && (
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-2 block">Días a agregar</label>
                  <div className="flex gap-2 mb-3">
                    {[7, 15, 30, 90].map(d => (
                      <button key={d} onClick={() => setSubDays(d)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold border transition-all ${subDays === d ? "bg-yellow-500/15 border-yellow-500/25 text-yellow-400" : "border-white/[0.08] text-zinc-500 hover:border-white/20"}`}>
                        {d}d
                      </button>
                    ))}
                  </div>
                  <input
                    data-testid="extend-days-input"
                    type="number"
                    className="input-field w-full"
                    value={subDays}
                    min={1}
                    max={365}
                    onChange={e => setSubDays(parseInt(e.target.value) || 1)}
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Nueva fecha de vencimiento: <strong className="text-white">
                      {(() => {
                        const base = subModal.subscription_expires_at ? new Date(subModal.subscription_expires_at) : new Date();
                        const fut = base < new Date() ? new Date() : base;
                        fut.setDate(fut.getDate() + subDays);
                        return fut.toLocaleDateString("es-ES");
                      })()}
                    </strong>
                  </p>
                </div>
              )}

              {/* Mode B: Specific date */}
              {subMode === "date" && (
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-2 block">
                    Fecha de vencimiento exacta
                  </label>
                  <input
                    data-testid="sub-date-input"
                    type="date"
                    className="input-field w-full"
                    value={subDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => setSubDate(e.target.value)}
                    style={{ colorScheme: "dark" }}
                  />
                  <p className="text-xs text-zinc-400 mt-2">
                    Útil para migración de usuarios de WhatsApp con fechas específicas.
                  </p>
                </div>
              )}

              {/* Plan selector */}
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-2 block">Plan</label>
                <div className="flex gap-2">
                  {[
                    { val: "premium", label: "Premium", color: "text-blue-400 border-blue-500/25 bg-blue-500/10" },
                    { val: "vip",     label: "VIP",     color: "text-yellow-400 border-yellow-500/25 bg-yellow-500/10" },
                    { val: "free",    label: "Free",    color: "text-zinc-400 border-zinc-500/20 bg-zinc-500/10" },
                  ].map(p => (
                    <button key={p.val} onClick={() => setSubPlan(p.val)}
                      className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase border transition-all ${
                        subPlan === p.val ? p.color : "border-white/[0.06] text-zinc-600 hover:text-zinc-400"
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Confirm button */}
              <button
                data-testid="sub-confirm-btn"
                onClick={applySubscription}
                disabled={subLoading || (subMode === "date" && !subDate)}
                className="w-full py-3.5 rounded-xl font-black text-sm uppercase tracking-wider transition-all disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #D4AF37, #B8962E)", color: "#000" }}>
                {subLoading ? "Aplicando..." : subMode === "days"
                  ? `Agregar ${subDays} días · ${subPlan.toUpperCase()}`
                  : `Establecer fecha ${subDate} · ${subPlan.toUpperCase()}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment History Modal ──────────────────────────────────────────── */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) setSelectedUser(null); }}>
          <div className="rounded-2xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col" style={{ background: "#1C1C22", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-lg">Historial de pagos</h3>
                <p className="text-sm text-zinc-500">{selectedUser.name} · {selectedUser.email}</p>
              </div>
              <button onClick={() => setSelectedUser(null)} className="text-zinc-500 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              {paymentsLoading ? (
                <div className="text-center text-zinc-500 py-8">Cargando...</div>
              ) : payments.length === 0 ? (
                <div className="text-center text-zinc-500 py-8">
                  <CreditCard className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <p>Sin pagos registrados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {payments.map((p, i) => (
                    <div key={p.id || i} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-xl">
                      <div>
                        <div className="text-sm font-semibold">{p.description || "Suscripción"}</div>
                        <div className="text-xs text-zinc-500">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString("es-ES") : "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-emerald-400">${(p.amount || 0).toFixed(2)}</div>
                        <span className={`text-[10px] font-bold uppercase ${p.payment_status === "paid" || p.status === "complete" ? "text-emerald-400" : "text-red-400"}`}>
                          {p.payment_status === "paid" ? "Pagado" : p.status || "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Crear Usuario ─────────────────────────────── */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="w-full max-w-md rounded-2xl p-6 space-y-5" style={{ background: "#16161A", border: "1px solid rgba(212,175,55,0.3)" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus weight="duotone" className="w-5 h-5 text-yellow-400" />
                <h3 className="font-bold text-lg">Crear nuevo usuario</h3>
              </div>
              <button onClick={() => setCreateModal(false)} className="text-zinc-500 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Nombre completo *</label>
                <input
                  data-testid="create-user-name"
                  type="text"
                  placeholder="Ej: Juan Pérez"
                  value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:ring-1 focus:ring-yellow-400/40"
                  style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Email *</label>
                <input
                  data-testid="create-user-email"
                  type="email"
                  placeholder="cliente@email.com"
                  value={createForm.email}
                  onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:ring-1 focus:ring-yellow-400/40"
                  style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Contraseña *</label>
                <input
                  data-testid="create-user-password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={createForm.password}
                  onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:ring-1 focus:ring-yellow-400/40"
                  style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Plan</label>
                  <select
                    data-testid="create-user-plan"
                    value={createForm.plan}
                    onChange={e => setCreateForm(f => ({ ...f, plan: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:ring-1 focus:ring-yellow-400/40"
                    style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium (7 días)</option>
                    <option value="vip">VIP (30 días)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Vence el (opcional)</label>
                  <input
                    data-testid="create-user-expires"
                    type="date"
                    value={createForm.expires_at}
                    onChange={e => setCreateForm(f => ({ ...f, expires_at: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:ring-1 focus:ring-yellow-400/40"
                    style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setCreateModal(false)}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-zinc-400 hover:text-white transition-colors"
                style={{ background: "rgba(255,255,255,0.05)" }}
              >
                Cancelar
              </button>
              <button
                data-testid="create-user-submit"
                onClick={handleCreateUser}
                disabled={createLoading}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-black transition-all hover:brightness-110 disabled:opacity-60"
                style={{ background: "#D4AF37" }}
              >
                {createLoading ? "Creando..." : "Crear Usuario"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Cambiar Contraseña ─────────────────────────────────────────── */}
      {pwModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) { setPwModal(null); setNewPassword(""); } }}>
          <div className="rounded-2xl p-6 w-full max-w-sm" style={{ background: "#1C1C22", border: "1px solid rgba(59,130,246,0.3)", boxShadow: "0 20px 60px rgba(0,0,0,0.7)" }}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Key className="w-4 h-4 text-blue-400" />
                  <h3 className="font-bold text-white text-base">Cambiar Contraseña</h3>
                </div>
                <p className="text-xs text-zinc-400">Usuario: <span className="text-white font-semibold">{pwModal.name}</span></p>
                <p className="text-xs text-zinc-500">{pwModal.email}</p>
              </div>
              <button onClick={() => { setPwModal(null); setNewPassword(""); }} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-5">
              <label className="text-xs text-zinc-400 mb-1.5 block">Nueva contraseña (mínimo 6 caracteres)</label>
              <input
                data-testid="admin-change-password-input"
                type="text"
                placeholder="Escribe la nueva contraseña..."
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleChangePassword()}
                className="w-full rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:ring-1 focus:ring-blue-400/40"
                style={{ background: "#0A0A0C", border: "1px solid rgba(255,255,255,0.1)" }}
                autoFocus
              />
              <p className="text-[11px] text-zinc-600 mt-1.5">El usuario podrá ingresar con esta contraseña inmediatamente.</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setPwModal(null); setNewPassword(""); }}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-zinc-400 hover:text-white transition-colors"
                style={{ background: "rgba(255,255,255,0.05)" }}
              >
                Cancelar
              </button>
              <button
                data-testid="admin-change-password-submit"
                onClick={handleChangePassword}
                disabled={pwLoading || newPassword.length < 6}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-50"
                style={{ background: "#3B82F6" }}
              >
                {pwLoading ? "Guardando..." : "Guardar contraseña"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
